/** Display helpers specific to the Home screen. Locale-aware via Intl; the
 *  locale defaults to English so pure-module callers and tests stay stable. */

import type { Locale } from '../../i18n/locale'

/**
 * `Intl.DateTimeFormat` construction is expensive and the Home page re-renders
 * on a 10s poll, so instances are cached per (locale, options) — mirrors the
 * caching in `i18n/format.ts`.
 */
const dateTimeCache = new Map<string, Intl.DateTimeFormat>()

function dtf(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = dateTimeCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    dateTimeCache.set(key, formatter)
  }
  return formatter
}

/** "4m 12s"-style duration (the design's stat + result-row format). The unit
 *  letters follow the codebase convention (see `format.ts` `formatDuration`)
 *  and are the same in every locale; the values are small padded integers with
 *  no locale-sensitive separators. */
export function fmtDurationClock(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return '—'
  const s = Math.round(ms / 1000)
  if (s < 1) return '<1s'
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`
  const h = Math.floor(m / 60)
  return `${h}h ${String(m % 60).padStart(2, '0')}m`
}

/**
 * A friendly first name for the greeting. Registration often leaves
 * displayName equal to the email — never greet someone with a full address:
 * fall back to the email's local part, capitalized. When neither is usable the
 * caller-supplied `fallback` (a localized greeting stand-in) is returned as-is.
 */
export function firstNameFor(displayName: string | undefined, email: string | undefined, fallback = 'there'): string {
  const fromDisplay = displayName && !displayName.includes('@') ? displayName.trim().split(/\s+/)[0] : undefined
  const fromEmail = email?.split('@')[0]
  const name = fromDisplay || fromEmail
  if (!name) return fallback
  return name.charAt(0).toUpperCase() + name.slice(1)
}

/** "Jul 4" (en) · "4 de jul." (pt-BR) from a YYYY-MM-DD local-calendar key
 *  (parsed as UTC on purpose). */
export function fmtDayShort(dayKey: string, locale: Locale = 'en'): string {
  return dtf(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${dayKey}T00:00:00Z`))
}

/** Long month name for the stats band: "July" (en) · "julho" (pt-BR). */
export function fmtMonthLong(date: Date, locale: Locale = 'en'): string {
  return dtf(locale, { month: 'long' }).format(date)
}

/** The hero kicker's date: "Monday, July 20" (en) · "segunda-feira, 20 de julho" (pt-BR). */
export function fmtToday(date: Date, locale: Locale = 'en'): string {
  return dtf(locale, { weekday: 'long', month: 'long', day: 'numeric' }).format(date)
}

/** Saturday/Sunday check for a YYYY-MM-DD local-calendar key. */
export function isWeekend(dayKey: string): boolean {
  const dow = new Date(`${dayKey}T00:00:00Z`).getUTCDay()
  return dow === 0 || dow === 6
}
